import type {
  AnimBeginActionNode,
  AnimEndActionNode,
  AnimKeyframeActionNode,
  AnimPlayActionNode,
  Port,
} from "../../../../util/src/blueprint/types.js";
import {
  getAvailableKeyframePortIds,
  isProtectedKeyframePortId,
  type AnimKeyframePortLabels,
  type KeyframeAddablePortId,
} from "../../../../util/src/blueprint/anim-keyframe-ports.js";
import { PortHandle } from "../PortHandle.js";
import { KeyframePortAddMenu } from "./KeyframePortAddMenu.js";
import dico from "../../../../util/src/language/dico.js";
import { useCallback, useMemo, useState } from "react";
import {
  Checkbox,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import type React from "react";

export type AnimationActionNode =
  | AnimBeginActionNode
  | AnimKeyframeActionNode
  | AnimEndActionNode
  | AnimPlayActionNode;

export interface ActionAnimationNodeProps {
  node: AnimationActionNode;
  sceneAnimationNames?: string[];
  setPortsMap: (
    nodeId: string,
    portId: string,
    element: HTMLElement | null,
  ) => void;
  onPortMouseDown?: (nodeId: string, port: Port) => void;
  onPortMouseUp?: (nodeId: string, port: Port) => void;
  isPortConnected?: (nodeId: string, portId: string) => boolean;
  onPortValueChange?: (nodeId: string, portId: string, value: unknown) => void;
  onPlayInfiniteChange?: (nodeId: string, playInfinite: boolean) => void;
  /** Adds a property/easing port on every keyframe of the animation. */
  onAddKeyframePort?: (portId: KeyframeAddablePortId) => void;
  /** Removes a property/easing port on every keyframe of the animation. */
  onRemoveKeyframePort?: (portId: string) => void;
  readOnly?: boolean;
}

type PortRow = { input?: Port; output?: Port };

type KeyframePortMenuState = {
  anchor: HTMLElement;
  portId: string;
};

function buildPortRows(node: AnimationActionNode): PortRow[] {
  const hiddenPortIds = new Set(
    node.kind === "animPlay" || node.kind === "animBegin" ? ["name"] : [],
  );
  if (node.kind === "animPlay" && node.playInfinite) {
    hiddenPortIds.add("out");
  }
  const ports = node.ports.filter((port) => !hiddenPortIds.has(port.id));
  const flowIn = ports.find((p) => p.id === "in");
  const flowOut = ports.find((p) => p.id === "out");
  const rest = ports.filter((p) => p.id !== "in" && p.id !== "out");

  if (node.kind === "animBegin") {
    const durationMs = rest.find((port) => port.id === "durationMs");
    const otherRest = rest.filter((port) => port.id !== "durationMs");
    const rows: PortRow[] = [];

    if (durationMs && flowOut) {
      rows.push({ input: durationMs, output: flowOut });
    } else if (flowOut) {
      rows.push({ output: flowOut });
    } else if (durationMs) {
      rows.push({ input: durationMs });
    }

    for (const port of otherRest) {
      rows.push({ input: port });
    }

    return rows;
  }

  const rows: PortRow[] = [];

  if (flowIn && flowOut) {
    rows.push({ input: flowIn, output: flowOut });
  } else if (flowIn) {
    rows.push({ input: flowIn });
  } else if (flowOut) {
    rows.push({ output: flowOut });
  }

  if (node.kind === "animPlay") {
    for (const p of rest) {
      rows.push({ input: p });
    }
    return rows;
  }

  if (node.kind === "animEnd") {
    return rows;
  }

  // animKeyframe: mirror ports are stored as consecutive input / output pairs.
  for (let i = 0; i < rest.length; i += 2) {
    const a = rest[i];
    const b = rest[i + 1];
    if (a && b && a.direction === "input" && b.direction === "output") {
      rows.push({ input: a, output: b });
    } else if (a) {
      rows.push({ input: a });
    }
  }

  return rows;
}

function buildKeyframePortLabels(): AnimKeyframePortLabels {
  const anim = dico().behavior.animation;
  return {
    flowIn: dico().behavior.actions.flowIn,
    flowOut: dico().behavior.actions.flowOut,
    offset: anim.offset,
    offsetOut: anim.offsetOut,
    easing: anim.easing,
    easingOut: anim.easingOut,
    mirrorSuffix: anim.mirrorSuffix,
    props: anim.props,
  };
}

/**
 * Blueprint nodes for declarative animation chains (`animBegin`, `animKeyframe`, `animEnd`).
 * Keyframe nodes expose a dynamic property list (add via +, remove via context menu)
 * synchronized across every keyframe of the same animation.
 */
export const ActionAnimationNode = ({
  node,
  sceneAnimationNames = [],
  setPortsMap,
  onPortMouseDown,
  onPortMouseUp,
  isPortConnected,
  onPortValueChange,
  onPlayInfiniteChange,
  onAddKeyframePort,
  onRemoveKeyframePort,
  readOnly = false,
}: ActionAnimationNodeProps) => {
  const anim = dico().behavior.animation;
  const title =
    node.kind === "animBegin"
      ? anim.begin
      : node.kind === "animKeyframe"
        ? anim.keyframe
        : node.kind === "animEnd"
          ? anim.end
          : anim.play;

  const headerBg =
    "linear-gradient(180deg, #c4b5fd 0%, #7c3aed 55%, #5b21b6 100%)";

  const rows = useMemo(() => buildPortRows(node), [node]);
  const keyframeLabels = useMemo(() => buildKeyframePortLabels(), []);
  const availablePortIds = useMemo(
    () =>
      node.kind === "animKeyframe" ? getAvailableKeyframePortIds(node) : [],
    [node],
  );

  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
  const [portMenu, setPortMenu] = useState<KeyframePortMenuState | null>(null);

  const currentName = String(
    node.ports.find((port) => port.id === "name")?.value ?? "",
  );
  const isInvalidName =
    node.kind === "animPlay" &&
    currentName.trim().length > 0 &&
    !sceneAnimationNames.some(
      (name) => name.toLocaleLowerCase() === currentName.toLocaleLowerCase(),
    );

  const canEditKeyframePorts =
    node.kind === "animKeyframe" &&
    !readOnly &&
    Boolean(onAddKeyframePort || onRemoveKeyframePort);

  const handleAddClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setAddAnchor(event.currentTarget);
    },
    [],
  );

  const handlePortContextMenu = useCallback(
    (event: React.MouseEvent, portId: string) => {
      if (!canEditKeyframePorts || !onRemoveKeyframePort) return;
      const baseId = portId.endsWith("Out")
        ? portId.replace(/Out$/, "")
        : portId;
      if (isProtectedKeyframePortId(baseId)) return;
      event.preventDefault();
      event.stopPropagation();
      setPortMenu({
        anchor: event.currentTarget as HTMLElement,
        portId: baseId,
      });
    },
    [canEditKeyframePorts, onRemoveKeyframePort],
  );

  const renderPort = (port: Port) => {
    const showValue = port.direction === "input" && port.valueType !== "flow";

    return (
      <PortHandle
        node={node}
        port={port}
        setPortsMap={setPortsMap}
        onMouseDown={() => onPortMouseDown?.(node.id, port)}
        onMouseUp={() => onPortMouseUp?.(node.id, port)}
        showValueInput={showValue}
        onValueChange={(value) => onPortValueChange?.(node.id, port.id, value)}
        onValueBlur={() => {}}
        isConnected={isPortConnected?.(node.id, port.id) ?? false}
        onContextMenu={
          node.kind === "animKeyframe"
            ? (event) => handlePortContextMenu(event, port.id)
            : undefined
        }
      />
    );
  };

  return (
    <div
      style={{
        minWidth: node.kind === "animKeyframe" ? 320 : 280,
        borderRadius: 8,
        overflow: "hidden",
        background:
          "linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(200, 200, 200, 0.98) 100%)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        border: "1px solid #e5e7eb",
        ...(node.kind === "animPlay" && isInvalidName
          ? { borderColor: "#d32f2f" }
          : {}),
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          padding: "4px 8px",
          fontSize: 12,
          fontWeight: 700,
          color: "#fff",
          background: headerBg,
          textAlign: node.kind === "animPlay" ? "left" : "center",
        }}
      >
        <span>{title}</span>
        {node.kind === "animPlay" ? (
          <span style={{ fontWeight: 600, opacity: 0.95 }}>
            {currentName.trim() || "-"}
          </span>
        ) : null}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          padding: "8px",
        }}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {rows.map((row, idx) => {
          const paired = Boolean(row.input && row.output);
          const contextPortId = row.input?.id ?? row.output?.id;

          return (
            <div
              key={`row-${idx}-${row.input?.id ?? ""}-${row.output?.id ?? ""}`}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                width: "100%",
                gap: 10,
                minHeight: 32,
              }}
              onContextMenu={
                contextPortId && node.kind === "animKeyframe"
                  ? (event) => handlePortContextMenu(event, contextPortId)
                  : undefined
              }
            >
              {row.input ? (
                <div
                  style={{
                    flex: paired ? 1 : undefined,
                    width: paired || row.output ? undefined : "100%",
                    minWidth: 0,
                    display: "flex",
                    justifyContent: "flex-start",
                  }}
                >
                  {renderPort(row.input)}
                </div>
              ) : null}
              {row.output ? (
                <div
                  style={{
                    flex: paired || row.input ? 1 : undefined,
                    width: row.input ? undefined : "100%",
                    minWidth: 0,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  {renderPort(row.output)}
                </div>
              ) : null}
            </div>
          );
        })}
        {node.kind === "animPlay" ? (
          <FormControlLabel
            sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: 12 } }}
            control={
              <Checkbox
                size="small"
                checked={Boolean(node.playInfinite)}
                onChange={(event) =>
                  onPlayInfiniteChange?.(node.id, event.target.checked)
                }
              />
            }
            label={anim.playInfinite}
          />
        ) : null}
        {canEditKeyframePorts && onAddKeyframePort ? (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              paddingTop: 2,
            }}
          >
            <IconButton
              size="small"
              aria-label={anim.addPort}
              onClick={handleAddClick}
              sx={{ p: 0.25 }}
            >
              <AddIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </div>
        ) : null}
      </div>
      {canEditKeyframePorts && onAddKeyframePort ? (
        <KeyframePortAddMenu
          anchorEl={addAnchor}
          open={Boolean(addAnchor)}
          availablePortIds={availablePortIds}
          labels={keyframeLabels}
          onClose={() => setAddAnchor(null)}
          onSelect={onAddKeyframePort}
        />
      ) : null}
      {canEditKeyframePorts && onRemoveKeyframePort ? (
        <Menu
          anchorEl={portMenu?.anchor ?? null}
          open={Boolean(portMenu)}
          onClose={() => setPortMenu(null)}
        >
          <MenuItem
            onClick={() => {
              if (portMenu) onRemoveKeyframePort(portMenu.portId);
              setPortMenu(null);
            }}
            sx={{ fontSize: 12, color: "error.main" }}
          >
            {anim.deletePort}
          </MenuItem>
        </Menu>
      ) : null}
    </div>
  );
};
